import type { User } from "@/types";

export interface RecentSearchState {
  query: string;
  criteria: Record<string, any>;
  location?: string | null;
  timestamp?: number;
}

const LAST_QUERY = "homeai_last_query";
const LAST_CRITERIA = "homeai_last_criteria";
const SEARCH_HISTORY = "homeai_search_history";

function userSuffix(user?: Pick<User, "id"> | null) {
  return user ? `user_${user.id}` : "anonymous";
}

function key(base: string, user?: Pick<User, "id"> | null) {
  return `${base}_${userSuffix(user)}`;
}

export function getRecentSearch(user?: Pick<User, "id"> | null): RecentSearchState | null {
  if (typeof window === "undefined") return null;
  try {
    const history = getRecentSearchHistory(user);
    if (history[0]) return history[0];
    const query = window.localStorage.getItem(key(LAST_QUERY, user)) || "";
    const criteriaRaw = window.localStorage.getItem(key(LAST_CRITERIA, user));
    const criteria = criteriaRaw ? JSON.parse(criteriaRaw) : {};
    if (!query && !criteriaRaw) return null;
    return { query, criteria, location: inferLocation(criteria, query), timestamp: Date.now() };
  } catch {
    return null;
  }
}

export function setRecentSearch(user: Pick<User, "id"> | null | undefined, state: { query?: string; criteria?: object }) {
  if (typeof window === "undefined") return;
  try {
    const query = state.query || "";
    const criteria = state.criteria || {};
    if (state.query !== undefined) window.localStorage.setItem(key(LAST_QUERY, user), state.query);
    if (state.criteria !== undefined) window.localStorage.setItem(key(LAST_CRITERIA, user), JSON.stringify(state.criteria));
    if (query || Object.keys(criteria).length > 0) {
      const next: RecentSearchState = {
        query,
        criteria,
        location: inferLocation(criteria, query),
        timestamp: Date.now(),
      };
      const prior = getRecentSearchHistory(user);
      const deduped = prior.filter(item => normalizedSearchKey(item) !== normalizedSearchKey(next));
      window.localStorage.setItem(key(SEARCH_HISTORY, user), JSON.stringify([next, ...deduped].slice(0, 5)));
    }
    window.localStorage.removeItem(LAST_QUERY);
    window.localStorage.removeItem(LAST_CRITERIA);
  } catch {}
}

export function getRecentSearchHistory(user?: Pick<User, "id"> | null): RecentSearchState[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(key(SEARCH_HISTORY, user));
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is RecentSearchState => Boolean(item && typeof item === "object"))
      .map(item => ({
        query: String(item.query || ""),
        criteria: item.criteria && typeof item.criteria === "object" ? item.criteria : {},
        location: item.location || inferLocation(item.criteria || {}, item.query || ""),
        timestamp: typeof item.timestamp === "number" ? item.timestamp : undefined,
      }))
      .filter(item => item.query || Object.keys(item.criteria).length > 0)
      .slice(0, 5);
  } catch {
    return [];
  }
}

export function clearRecentSearch(user?: Pick<User, "id"> | null) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(key(LAST_QUERY, user));
    window.localStorage.removeItem(key(LAST_CRITERIA, user));
    window.localStorage.removeItem(key(SEARCH_HISTORY, user));
    window.localStorage.removeItem(LAST_QUERY);
    window.localStorage.removeItem(LAST_CRITERIA);
  } catch {}
}

function inferLocation(criteria: Record<string, any> | undefined, query: string) {
  const direct = criteria?.city;
  if (typeof direct === "string" && direct.trim()) return direct.trim();
  const cities = criteria?.cities;
  if (Array.isArray(cities) && typeof cities[0] === "string") return cities[0];
  const match = String(query || "").match(/\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\b/);
  return match?.[1] || null;
}

function normalizedSearchKey(item: RecentSearchState) {
  const location = (item.location || "").toLowerCase();
  const query = (item.query || "").trim().toLowerCase();
  return `${location}|${query}`;
}
